import { PropertySharingAction } from 'src/modules/shared/components/molecules/property-sharing-action';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { useNavigate } from '@tanstack/react-router';
import React from 'react';
import { PiFloppyDisk } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { UnsavedChangesModal } from 'src/modules/shared/components/molecules/modals/unsaved-changes-modal';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PageHeader } from 'src/modules/shared/components/molecules/page-header';
import { ConfirmModal } from 'src/modules/shared/components/organisms/modal';
import {
  useCreateProperty,
  useUpdateProperty,
} from 'src/modules/shared/hooks/api/catalog/properties.mutation';
import { useProperty } from 'src/modules/shared/hooks/api/catalog/properties';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useUnsavedNavigationGuardState } from 'src/modules/shared/hooks/unsaved-navigation-guard';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useAlert } from 'src/contexts/alert-context';
import {
  IntegrationPermission,
  PropertyPermission,
} from 'src/modules/shared/types/permissions.type';
import type { PropertyEditorPageProps } from '../../types/property-editor-page.type';
import { usePropertyEditorBack } from '../../hooks/use-property-editor-back';
import { PropertyEditorPageSkeleton } from '../molecules/property-editor-page-skeleton';
import { PropertyEditorUnavailableState } from '../molecules/property-editor-unavailable-state';
import {
  getPropertyEditorPageState,
  usePropertyEditorPublishingState,
  usePropertyEditorSubmitFlow,
} from '../../hooks/use-property-editor-page';
import { PropertyEditorForm } from '../organisms/property-editor-form';
import { PropertyPublishingPanel } from '../organisms/property-publishing-panel';
import { useIntegrations } from 'src/modules/shared/hooks/api/integrations/integrations';

/** Renders property editing with permissions and guarded return navigation. */
export const PropertyEditorPage: React.FC<PropertyEditorPageProps> = ({
  propertyId,
  backSearch,
}) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showAlert } = useAlert();
  const isEdit = typeof propertyId === 'number';
  const formId = 'property-editor-form';
  const guard = useUnsavedNavigationGuardState(false);
  const { hasPermission: canReadAll, loading: loadingPropertyPermissions } =
    useHasPermissions(PropertyPermission.Read);
  const { hasPermission: canReadScoped } = useHasPermissions(
    PropertyPermission.ReadScoped
  );
  const { hasPermission: canCreateProperties, loading: loadingPropertyCreate } =
    useHasPermissions(PropertyPermission.Create);
  const { hasPermission: canUpdateAll, loading: loadingPropertyUpdate } =
    useHasPermissions(PropertyPermission.Update);
  const { hasPermission: canUpdateScoped } = useHasPermissions(
    PropertyPermission.UpdateScoped
  );
  const { user } = useCurrentUser();
  const canReadProperties = canReadAll || canReadScoped;
  const {
    hasPermission: canReadPublications,
    loading: loadingPublicationPermissions,
  } = useHasPermissions(IntegrationPermission.Read);
  const { integrations, loading: loadingIntegrations } = useIntegrations(
    canReadPublications && !loadingPublicationPermissions
  );
  const {
    property,
    loading: loadingProperty,
    error: propertyError,
  } = useProperty(propertyId ?? 0, isEdit && canReadProperties);
  const { createProperty, loading: creatingProperty } = useCreateProperty();
  const { updateProperty, loading: updatingProperty } = useUpdateProperty();

  const isSaving = creatingProperty || updatingProperty;
  const {
    loadingPublications,
    hasActivePropertySyncIntegration,
    shouldRenderPublishingPanel,
    republishablePublicationCount,
  } = usePropertyEditorPublishingState({
    property,
    integrations,
  });
  const isInScope = user != null && property?.userId === user.id;
  const canUpdateProperty = canUpdateAll || (canUpdateScoped && isInScope);
  const canSaveProperty = isEdit ? canUpdateProperty : canCreateProperties;
  const {
    pendingSubmitValues,
    setPendingSubmitValues,
    isPendingDeactivation,
    handleSubmitWithErrors,
    handleConfirmPendingSubmit,
  } = usePropertyEditorSubmitFlow({
    isEdit,
    propertyId,
    property,
    republishablePublicationCount,
    createProperty,
    updateProperty,
    guard,
    showAlert,
    t,
    navigate,
    backSearch,
  });

  const confirmationKey = isPendingDeactivation
    ? 'confirmDeactivate'
    : 'confirmRepublish';

  const labels = isEdit
    ? {
        title: t('catalog.properties.editor.editTitle'),
        subtitle: t('catalog.properties.editor.editSubtitle'),
        submit: t('catalog.properties.editor.save'),
      }
    : {
        title: t('catalog.properties.editor.createTitle'),
        subtitle: t('catalog.properties.editor.createSubtitle'),
        submit: t('catalog.properties.editor.create'),
      };

  const handleBack = usePropertyEditorBack(backSearch);

  const pageState = getPropertyEditorPageState({
    loadingPropertyPermissions,
    loadingPropertyCreate,
    loadingPropertyUpdate,
    loadingPublicationPermissions,
    loadingProperty,
    loadingIntegrations,
    loadingPublications,
    property,
    isEdit,
    canReadProperties: isEdit ? canReadProperties : canCreateProperties,
    propertyError,
  });

  if (pageState === 'loading') {
    return (
      <PropertyEditorPageSkeleton
        showRail={isEdit || !hasActivePropertySyncIntegration}
      />
    );
  }

  if (pageState !== 'ready') {
    return (
      <PropertyEditorUnavailableState
        pageState={pageState}
        onBack={handleBack}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <UnsavedChangesModal
        isOpen={guard.isBlocked}
        onProceed={guard.proceed}
        onStay={guard.stay}
        isSaving={isSaving}
      />
      <ConfirmModal
        isOpen={pendingSubmitValues != null}
        onClose={() => setPendingSubmitValues(null)}
        onConfirm={handleConfirmPendingSubmit}
        confirmText={t(`catalog.properties.editor.${confirmationKey}.confirm`)}
        cancelText={t(`catalog.properties.editor.${confirmationKey}.cancel`)}
        loading={isSaving}
        title={t(`catalog.properties.editor.${confirmationKey}.title`)}
      >
        {isPendingDeactivation ? (
          <div className="space-y-3">
            <Text>
              {t('catalog.properties.editor.confirmDeactivate.description')}
            </Text>
            {republishablePublicationCount > 0 ? (
              <Text>
                {t('catalog.properties.confirmDeactivate.publicationImpact')}
              </Text>
            ) : null}
          </div>
        ) : (
          <Text>
            {t('catalog.properties.editor.confirmRepublish.description', {
              count: `${republishablePublicationCount}`,
            })}
          </Text>
        )}
      </ConfirmModal>

      <Panel className="flex h-full min-h-0 flex-col">
        <PageHeader
          title={labels.title}
          subtitle={labels.subtitle}
          onBack={handleBack}
          backLabel={t('shared.actions.back')}
          actions={
            <div className="flex flex-wrap items-center justify-end gap-2">
              <div role="status" hidden={!guard.hasUnsavedChanges}>
                <Text size="tiny" className="text-slate-500">
                  {t('catalog.properties.editor.unsavedChanges')}
                </Text>
              </div>
              {property && (
                <PropertySharingAction sharingLink={property.sharingLink} />
              )}
              <Button
                variant="primary"
                type="submit"
                form={formId}
                disabled={isSaving || !canSaveProperty}
                loading={isSaving}
                className="flex items-center gap-2"
              >
                <PiFloppyDisk className="h-4 w-4" />
                <Text size="small">{labels.submit}</Text>
              </Button>
            </div>
          }
        />

        <div className="min-h-0 flex-1 overflow-auto bg-slate-50">
          <div
            className={`mx-auto grid w-full max-w-7xl items-start grid-cols-1 gap-5 p-4 ${
              shouldRenderPublishingPanel
                ? 'xl:grid-cols-[minmax(0,1fr)_360px]'
                : ''
            }`}
          >
            <PropertyEditorForm
              formId={formId}
              property={property}
              loading={isSaving}
              onSubmit={handleSubmitWithErrors}
              onDirtyChange={guard.setHasUnsavedChanges}
            />

            {shouldRenderPublishingPanel ? (
              <PropertyPublishingPanel property={property} />
            ) : null}
          </div>
        </div>
      </Panel>
    </div>
  );
};
